import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { getMessages } from '../MessagesServices';
import { debounce } from '../../../Utils/debounce';
import { performanceMonitor } from '../../../Utils/performanceMonitor';
import useOptimizedMessages from './useOptimizedMessages';

const useMessageFetching = (selectedConversation, handleError) => {
    const {
        messages,
        replaceMessages,
        appendMessages,
        addMessage,
        updateMessage,
        deleteMessage
    } = useOptimizedMessages([]);
    const [loading, setLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isFetchingMoreTop, setIsFetchingMoreTop] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const messageRefs = useRef({});
    const perPage = 10;

    const fetchMessages = useCallback(async (page = 1, shouldAppend = false, onScrollEnd = () => { }) => {
        if (!selectedConversation) return;

        return performanceMonitor.trackRender('useMessageFetching.fetchMessages', async () => {
            const isInitial = page === 1 && !shouldAppend;
            if (isInitial) {
                setIsInitialLoading(true);
            }
            setLoading(true);
            try {
                const { data, metadata } = await getMessages({
                    conversation_id: selectedConversation.id,
                    page,
                    size: perPage,
                });
                onScrollEnd();
                setHasMore(metadata.has_next);
                if (shouldAppend) {
                    appendMessages(data);
                } else {
                    replaceMessages(data);
                }
                setCurrentPage(page);
            } catch (error) {
                handleError(error);
                setHasMore(false);
            } finally {
                setLoading(false);
                if (isInitial) {
                    setIsInitialLoading(false);
                }
            }
        });
    }, [selectedConversation, handleError]);

    // Store current state in refs to avoid stale closures
    const stateRef = useRef();
    stateRef.current = { loading, hasMore, fetchMessages, currentPage };

    // Optimize: Debounced scroll handler to prevent excessive API calls
    const debouncedFetchMore = useMemo(
        () => debounce((container) => {
            const { loading: currentLoading, hasMore: currentHasMore, fetchMessages: currentFetchMessages, currentPage: currentPageValue } = stateRef.current;

            if (!currentLoading && currentHasMore) {
                let prevScrollTop = 0;
                let prevScrollHeight = 0;
                setIsFetchingMoreTop(true);
                currentFetchMessages(
                    currentPageValue + 1,
                    true,
                    () => {
                        prevScrollTop = container.scrollTop;
                        prevScrollHeight = container.scrollHeight;
                    }
                ).then(() => {
                    requestAnimationFrame(() => {
                        const newScrollHeight = container.scrollHeight;
                        const delta = newScrollHeight - prevScrollHeight;
                        // Preserve visual position after prepending older messages
                        container.scrollTop = prevScrollTop + delta;
                    });
                }).finally(() => {
                    setIsFetchingMoreTop(false);
                });
            }
        }, 100),
        []
    );

    const handleScroll = useCallback((event) => {
        const container = event.currentTarget;
        const { scrollTop } = container;
        // Only fetch when scrolled to the absolute top
        if (scrollTop === 0) {
            debouncedFetchMore(container);
        }
    }, [debouncedFetchMore]);

    useEffect(() => {
        if (selectedConversation) {
            replaceMessages([]);
            setCurrentPage(1);
            setHasMore(true);
            fetchMessages(1, false);
        }
    }, [selectedConversation, fetchMessages, replaceMessages]);

    return {
        messages,
        loading,
        hasMore,
        messageRefs,
        handleScroll,
        addMessage,
        updateMessage,
        deleteMessage,
        isInitialLoading,
        isFetchingMoreTop
    };
};

export default useMessageFetching;